import React from "react";
import { useTranslation } from "react-i18next";
import { useDocTitle } from "../../components/_common/DocTitle";

import { useMatomo } from "@datapunt/matomo-tracker-react";

const Polityka_Page = () => {
    const { i18n, t } = useTranslation();
    useDocTitle(t("Polityka prywatności - Repozytorium Systemów Informacyjnych"));
    // Track page view
    const { trackPageView } = useMatomo();
    React.useEffect(() => {
        trackPageView();
        // eslint-disable-next-line
    }, []);

    return i18n?.language === "pl" ? (
        <>
            <h2>Polityka prywatności witryny Repozytorium Systemów Informacyjnych</h2>
            <p>
                Przywiązujemy szczególną wagę do poszanowania prywatności użytkowników odwiedzających witrynę{" "}
                <a href="https://rsi.stat.gov.pl/">Repozytorium Systemów Informacyjnych</a> (zwaną dalej Witryną).
            </p>

            <h3>Pliki cookies</h3>
            <p>
                W celu świadczenia usług na najwyższym poziomie oraz dostosowania Witryny do potrzeb użytkowników
                stosujemy pliki cookies (tzw. ciasteczka) pochodzące:
            </p>
            <ul>
                <li>
                    z wewnętrznego narzędzia do analizy statystyk Matomo, umieszczane w urządzeniu użytkownika
                    końcowego;
                </li>
                <li>z zewnętrznego narzędzia, składowane na potrzeby komponentu mapowego Google Maps.</li>
            </ul>
            <p>Matomo na podstawie plików cookies otrzymuje m.in. następujące informacje o użytkowniku:</p>
            <ol>
                <li>
                    z jakiego źródła znalazł się na naszej stronie internetowej (np. inna witryna, wyszukiwarka
                    internetowa),
                </li>
                <li>na jakie podstrony się kierował,</li>
                <li>jakiej rozdzielczości używał,</li>
                <li>z jakiego typu urządzenia korzystał (np. komputer, tablet, smartphone),</li>
                <li>czy podczas korzystania z naszej strony internetowej wystąpiły problemy.</li>
            </ol>
            <p>
                Korzystanie z witryny oznacza, że będą one zapisywane w Twoim urządzeniu (komputerze, smartfonie itp.).
                W każdym momencie możesz dokonać zmiany ustawień przeglądarki i samodzielnie zarządzać plikami cookies.
            </p>

            <h3>Informacje zbierane podczas korzystania z witryny</h3>
            <p>
                Podczas odwiedzania Witryny nasz system automatycznie zapisuje dzienniki zdarzeń systemowych tzw. logi -
                zawierające następujące informacje:
            </p>
            <ol>
                <li>adres IP użytkownika,</li>
                <li>wykonywane zdarzenia/akcje,</li>
                <li>czas korzystania z witryny,</li>
                <li>kraj, z którego łączono się z Witryną,</li>
                <li>liczba odwiedzin,</li>
                <li>wiersz żądania http,</li>
                <li>kod odpowiedzi http,</li>
                <li>liczba wysłanych przez serwer bajtów,</li>
                <li>
                    adres URL strony poprzednio odwiedzanej przez użytkownika (referer link) - w przypadku gdy przejście
                    do Witryny nastąpiło przez odnośnik,
                </li>
                <li>informacje o przeglądarce internetowej,</li>
                <li>informacje o błędach, jakie nastąpiły przy realizacji transakcji http.</li>
            </ol>
            <p>
                Gromadzone w dziennikach logów informacje są wykorzystywane do celów zapewnienia ochrony, poufności i
                bezpieczeństwa danych statystycznych, w tym administrowania Witryną. Nie prowadzimy profilowania
                użytkowników. Szczegółowe informacje na temat przetwarzania danych osobowych zawarte są{" "}
                <a href="/files//klauzula_informacyjna.docx">w klauzuli informacyjnej</a>.
            </p>
            <p>
                Celem nawiązania kontaktu z administratorami Witryny i uzyskania pomocy należy przesłać wiadomość e-mail
                na adres <a href="mailto:rsi@stat.gov.pl">rsi@stat.gov.pl</a>.
            </p>
            <p>
                Jeżeli Witryna zawiera odnośniki do innych stron internetowych zachęcamy, by po przejściu na inną stronę
                internetową zapoznać się z jej polityką prywatności. Nie ponosimy odpowiedzialności za zasady zachowania
                prywatności obowiązujące na innych stronach internetowych.
            </p>
        </>
    ) : (
        <>
            <h2>Privacy policy of Information Systems Repository website</h2>
            <p>
                We attach particular importance to respecting the privacy of users visiting{" "}
                <a href="https://rsi.stat.gov.pl/en">Information Systems Repository</a> website
                (hereinafter referred to as the Website).
            </p>

            <h3>Cookies files</h3>
            <p>To provide services at the highest level and adapt the Website to users needs, we use cookies from:</p>
            <ul>
                <li>
                    the internal analytics tool Matomo used for statistical analysis, stored on the end user's device;
                </li>
                <li>an external tool, used for the Google Maps component.</li>
            </ul>

            <p>Matomo, based on cookies, collects information about the user, including:</p>
            <ol>
                <li>the source from which the user arrived at our website (e.g. another website, search engine),</li>
                <li>which subpages they visited,</li>
                <li>what screen resolution they used,</li>
                <li>what type of device they used (e.g. computer, tablet, smartphone),</li>
                <li>whether any problems occurred while using our website.</li>
            </ol>
            <p>
                Using the Website means that cookies will be stored on your device (computer, smartphone, etc.). You can
                change your browser settings at any time and manage cookies yourself.
            </p>

            <h3>Information collected during the use of the website</h3>
            <p>
                During visiting the Website, our system automatically saves system event logs containing the following
                information:
            </p>
            <ol>
                <li>user's IP address,</li>
                <li>performed events/actions,</li>
                <li>duration of using the Website,</li>
                <li>country from which the connection to the Website was made,</li>
                <li>number of visits,</li>
                <li>HTTP request line,</li>
                <li>HTTP response code,</li>
                <li>number of bytes sent by the server,</li>
                <li>
                    URL of the website previously visited by the user (referer link) - if the visit to the Website was
                    made via a hyperlink,
                </li>
                <li>information about the web browser,</li>
                <li>information about errors that occurred during the execution of the HTTP transaction.</li>
            </ol>
            <p>
                The information collected in log files is used for the purposes of ensuring the protection,
                confidentiality, and security of statistical data, including the administration of the Website. We do
                not profile users. Detailed information regarding the processing of personal data is provided{" "}
                <a href="/files//klauzula_informacyjna.docx">in the information clause</a>.
            </p>
            <p>
                To contact the administrators of the Website and obtain assistance, please send an email to{" "}
                <a href="mailto:rsi@stat.gov.pl">rsi@stat.gov.pl</a>.
            </p>
            <p>
                If the Website contains links to other websites, we encourage you to review the privacy policy of those
                websites after navigating to them. We are not responsible for the privacy practices of other websites.
            </p>
        </>
    );
};

export default Polityka_Page;
